/**
 * The date of birth, and nothing else in this codebase.
 *
 * A DOB is the one value here with no instant behind it. `2002-12-21` is a day
 * on a card in somebody's wallet; it is not "midnight somewhere", and the moment
 * it becomes one it starts moving. `Date.parse('2002-12-21')` is *defined* to
 * mean midnight UTC, and `Intl.DateTimeFormat(undefined, …)` renders in the
 * viewer's zone — put those two lines together, which the customer drawer did,
 * and every operator west of Greenwich reads a birthday one day early. Nothing
 * about the result looks wrong: it is a plausible date, formatted correctly,
 * off by one.
 *
 * So the digits are never converted. They are matched, checked against a
 * calendar, and re-emitted as themselves. The only thing borrowed from `Intl` is
 * the twelve month NAMES, and those are looked up from a constant that has
 * nothing to do with any customer's value.
 *
 * ── Why this lives in @acs/shared ───────────────────────────────────
 * Three places need the same answer to "is this a usable date of birth": the
 * drawer that displays it, the edit form that seeds three text boxes from it,
 * and the API mapper that decides what the DTO carries. One definition, or they
 * drift. It is also the only way the display rule is provable at all — the
 * `*-check.ts` scripts live in `apps/api` and cannot import a `.tsx` file out of
 * `apps/web`, but they can import this. See `scripts/dob-check.ts`.
 *
 * What is NOT here, deliberately: anything that would let a caller turn an
 * unreadable stored value into a date. There is no lenient parse and no
 * fallback, because the failure this module exists to prevent is a confident
 * wrong birthday, not a missing one.
 */

/** `YYYY-MM-DD` and only that: no time, no offset, no `Z`, no single digits. */
const DOB_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/

export interface DobParts {
  /** Four digits, exactly as stored. */
  year: string
  /** Two digits, `01`–`12`. */
  month: string
  /** Two digits, `01`–`31`. */
  day: string
}

/**
 * Days in a month, computed rather than looked up through `Date`.
 *
 * `new Date(Date.UTC(y, m, 0)).getUTCDate()` gives the same answer, and the
 * create form's calendar does exactly that — but that calendar is *choosing* a
 * date, while this is validating one somebody already has. This module's whole
 * claim is that no `Date` is ever constructed from a customer's value, and six
 * lines is a cheap price for a claim that holds by inspection.
 */
function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
    return leap ? 29 : 28
  }
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31
}

/**
 * The three fields of a stored date of birth, or `null` if it is not one.
 *
 * `null` covers absent, empty, a full ISO instant, `21/12/2002`, `2002-12-1`,
 * and `2002-02-31` — a day the shape allows and the calendar does not. They are
 * one outcome on purpose: every caller's answer to all of them is the same,
 * which is "do not render this as a date".
 */
export function parseDob(value: string | null | undefined): DobParts | null {
  if (typeof value !== 'string') return null
  const m = DOB_SHAPE.exec(value)
  if (m === null) return null

  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (month < 1 || month > 12) return null
  if (day < 1 || day > daysInMonth(year, month)) return null

  return { year: m[1] as string, month: m[2] as string, day: m[3] as string }
}

/**
 * The twelve month names, resolved once per locale.
 *
 * Pinned to UTC *and* taken from the 15th: the pin is what makes the label
 * correct, and the 15th means that even if a later edit dropped the pin, no zone
 * on earth could move a mid-month sentinel into a neighbouring month. The value
 * being formatted is a constant — a customer's date of birth never reaches
 * `Intl`, which is the single property that makes this whole module safe.
 *
 * Cached because it is twelve `format` calls, and because a formatter resolves
 * its timezone once at construction: this one resolved UTC explicitly, so unlike
 * an unpinned formatter it stays correct however long it is held.
 */
const monthLabelCache = new Map<string, readonly string[]>()

function monthLabels(locale: string | undefined): readonly string[] {
  const key = locale ?? ''
  const cached = monthLabelCache.get(key)
  if (cached !== undefined) return cached

  const fmt = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
  const labels = Object.freeze(
    Array.from({ length: 12 }, (_, i) => fmt.format(Date.UTC(2001, i, 15)))
  )
  monthLabelCache.set(key, labels)
  return labels
}

/**
 * `2002-12-21` → `Dec 21, 2002`. `null` for anything that is not a stored DOB.
 *
 * The day and the year are the stored characters, copied through — not
 * reconstructed from a timestamp — so there is no arithmetic that could move
 * them. The day is unpadded to match what `dateStyle: 'medium'` printed before.
 *
 * `null` rather than a fallback string, because only the caller knows whether an
 * unreadable value should read as "—" (nothing on file) or be shown verbatim
 * with a warning (something on file that we refuse to interpret), and collapsing
 * those two would hide a populated field behind a dash.
 *
 * ── The cost, so nobody rediscovers it as a bug ─────────────────────
 * Composing the string fixes the ORDER to `Mon D, YYYY` rather than following
 * the locale the way `dateStyle: 'medium'` did. The alternative — feeding
 * `Date.UTC(...)` built from these very digits into a formatter pinned to
 * `timeZone: 'UTC'` — is correct today and locale-ordered, but its correctness
 * hangs on one option key: delete the pin in some later refactor and the
 * original bug is back, with no type error and a date that still looks right.
 * This version cannot regress that way. If locale ordering ever matters, the
 * upgrade is `formatToParts` on a UTC-pinned formatter with these stored digits
 * substituted for the `day`/`year` parts.
 *
 * `locale` exists for `dob-check.ts`, which asserts literal strings and cannot
 * depend on the locale of the box it runs on. The app never passes it, so month
 * names follow the operator's browser like every other formatter in the panel.
 */
export function formatDob(value: string | null | undefined, locale?: string): string | null {
  const p = parseDob(value)
  if (p === null) return null
  const month = monthLabels(locale)[Number(p.month) - 1] as string
  return `${month} ${String(Number(p.day))}, ${p.year}`
}
